import React, { useState, useEffect, useLayoutEffect } from "react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { useSelector, useDispatch } from "react-redux";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import shared from "../../styles/UserProfileCommon.module.scss";
import Spinner from "../../components/Spinner";
import UserPayPortalForm from "./UserPayPortalForm";
import {
  getHWAccountStatus,
  getHWUserInfo,
} from "../../selectors/monetizationSelector";
import { HWAccountStatus } from "../../reducers/models";
import {
  fetchHyperwalletStatus,
} from "../../actions/monetization";
import classNames from "classnames";
import styles from "../../styles/admanager/UserAdSettings.module.scss";
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";
import selectCurrentState from "../../selectors/selectCurrentState";
import selectCurrentUser from "../../selectors/currentUserInfo";
import { WorkState } from "../../reducers/models";
import { openNewWindow } from "../../utils/helperUtils";
import isLoggingInFromClient from "../../selectors/isLoggingInFromClient";

export default function UserAdSettings() {
  const dispatch = useDispatch();
  const accountStatus = useSelector(getHWAccountStatus);
  const currentUser = useSelector(selectCurrentUser);
  const userInfo = useSelector(getHWUserInfo);
  const clientLoginState = useSelector(isLoggingInFromClient);
  const { status } = accountStatus;
  const [isRegistered, setIsRegistered] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const { workState } = useSelector(selectCurrentState);

  const [errorMessage, setErrorMessage] = useState(false);

  const setupWidgetRef = React.createRef(null);
  const supportWidgetRef = React.createRef(null);

  const spinnerStyle = {
    height: "50px",
    width: "50px",
  };
  useEffect(() => {
    if (currentUser) {
      const fetchHyperWalletData = async () => {
        await dispatch(fetchHyperwalletStatus());
      };
      fetchHyperWalletData();
    }
  }, [currentUser, dispatch]);

  useEffect(() => {
    if (workState.adSettings === WorkState.Error) {
      setErrorMessage(
        "There was a problem getting your information, please try again later"
      );
    }
  }, [currentUser, userInfo, workState.adSettings]);

  useLayoutEffect(() => {
    if (
      (status === HWAccountStatus.ACTIVATED ||
        status === HWAccountStatus.PRE_ACTIVATED) &&
      userInfo
    ) {
      setIsRegistered(true);
    } else {
      setIsRegistered(false);
    }
  }, [status, userInfo]);

  const closeWidget = (ref, id) => {
    ref.current.style.display = "none";
    localStorage.setItem(id, "true");
  };

  const AccountStatus = () => {
    let hyperWalletStatus;
    let description;
    if (status === HWAccountStatus.ACTIVATED) {
      hyperWalletStatus = "Activated";
      description = "Hyperwallet connected.";
    } else if (status === HWAccountStatus.PRE_ACTIVATED) {
      hyperWalletStatus = "Pending";
      description =
        "Thank you for signing up, please check your email to activate your account.";
    }
    return (
      <div className={styles.hyperwalletLogoWrapper}>
        <img
          src="https://frontend-assets.buildbox.com/hyperwallet.png"
          alt="Hyperwallet Logo"
          className={styles.hyperwalletLogo}
        />
        <div>
          {isRegistered ? (
            <div>
              <FontAwesomeIcon className={styles.checkIcon} icon={faCheck} />
              <span>{hyperWalletStatus}</span>
              <div className={styles.hyperwalletLogoText}>{description}</div>
            </div>
          ) : (
            <div
              className={classNames(
                styles.hyperwalletLogoText,
                styles.unregistered
              )}
            >
              Connect Hyperwallet to your account
            </div>
          )}
        </div>
      </div>
    );
  };

  const CurrentInfoWidget = () => {
    return (
      <div className={styles.currentInfoWidget}>
        <div className={styles.infoLabelWrapper}>
          <div className={styles.infoLabel}>First Name:</div>
          <div className={styles.infoLabel}>Last Name:</div>
          <div className={styles.infoLabel}>Date of Birth:</div>
          <div className={styles.infoLabel}>Country:</div>
          <div className={styles.infoLabel}>Email:</div>
        </div>

        <div className={styles.infoLabelWrapper}>
          <p>{userInfo.firstName || <Skeleton />}</p>
          <p>{userInfo.lastName || <Skeleton />}</p>
          <p>{userInfo.dateOfBirth || <Skeleton />}</p>
          <p>{userInfo.country || <Skeleton />}</p>
          <p>{userInfo.email || <Skeleton />}</p>
        </div>
      </div>
    );
  };

  const SetupWidget = () => {
    return (
      <div className={styles.messageWidget} ref={setupWidgetRef}>
        <div className={styles.iconWrapper}>
          <button
            className={styles.closeBtn}
            onClick={() => closeWidget(setupWidgetRef, "closedSetupWidget")}
          >
            <FontAwesomeIcon className={styles.closeIcon} icon={faTimes} />
          </button>
        </div>
        <h2 style={{ paddingLeft: "20px" }}>What's next? It’s super simple.</h2>
        <ul className={styles.setupInfoWrapper}>
          <li className={styles.progressCircleWrapper}>
            <div className={styles.progressCircle}>1</div>
          </li>
          <li className={styles.progressCircleWrapper}>
            <h3 className={styles.setupHeader}>
              You will receive an invitation from Hyperwallet to create an
              account.
            </h3>
            <p className={styles.setupText}>
              This is where you will set up your virtual wallet and recieve your
              revenue payouts.
            </p>
          </li>
          <li className={styles.progressCircleWrapper}>
            <div className={styles.progressCircle}>2</div>
          </li>
          <li className={styles.progressCircleWrapper}>
            <h3 className={styles.setupHeader}>
              When you have earned revenue you will receive an email from
              Hyperwallet.
            </h3>
            <p className={styles.setupText}>
              Every month Hyperwallet will send a confirmation email letting you
              know that funds have been deposited.
            </p>
          </li>
          <li className={styles.progressCircleWrapper}>
            <div className={styles.progressCircle}>3</div>
          </li>
          <li className={styles.progressCircleWrapper}>
            <h3 className={styles.setupHeader}>Get paid!</h3>
            <p className={styles.setupText}>
              That’s it, just relax, monetize and get payments every month.
            </p>
          </li>
        </ul>
      </div>
    );
  };

  const SupportWidget = () => {
    const openUrl = (e, url) => {
      e.preventDefault();
      if (clientLoginState) {
        openNewWindow(url, false);
      } else {
        window.open(url, "_blank");
      }
    };

    const contactSupportUrl = () => {
      if (clientLoginState) {
        return "/account/support";
      } else {
        return "mailto:support@buildbox.com?subject=Hyperwallet Activation Setup - Account Activation Status";
      }
    };

    return (
      <div
        className={classNames(styles.messageWidget, styles.support)}
        ref={supportWidgetRef}
      >
        <div className={styles.iconWrapper}>
          <button
            className={styles.closeBtn}
            onClick={() => closeWidget(supportWidgetRef, "closedSupportWidget")}
          >
            <FontAwesomeIcon className={styles.closeIcon} icon={faTimes} />
          </button>
        </div>
        <h2 style={{ paddingLeft: "36px" }}>
          Don’t know how to setup ads in your game?
        </h2>
        <ul className={styles.setupInfoWrapper}>
          <li className={styles.progressCircleWrapper}>
            <p className={styles.setupText} style={{ color: "#D6D6D6" }}>
              Check{" "}
              { /* TODO: change all of our <a tags without hrefs to be buttons styled as links */}
              {/* eslint-disable-next-line */}
              <a
                href=""
                onClick={(e) =>
                  openUrl(
                    e,
                    "https://www.buildbox.com/help/buildbox-3-manual/ad-monetization/"
                  )
                }
              >
                documentation
              </a>{" "}
              or this{" "}
              { /* TODO: change all of our <a tags without hrefs to be buttons styled as links */}
              {/* eslint-disable-next-line */}
              <a
                href=""
                onClick={(e) => openUrl(e, "https://youtu.be/E4yj7YIW1bI")}
              >
                tutorial video
              </a>{" "}
              to learn more about ads types, locations, settings, and best
              practices. If you still have questions or issues, feel free to
              <a href={contactSupportUrl()}> contact support </a>
              or check out our{" "}
              { /* TODO: change all of our <a tags without hrefs to be buttons styled as links */}
              {/* eslint-disable-next-line */}
              <a
                href=""
                onClick={(e) =>
                  openUrl(e, "https://discord.com/invite/buildbox")
                }
              >
                Discord
              </a>{" "}
              page.
            </p>
          </li>
        </ul>
      </div>
    );
  };

  const renderUI = () => {
    if (workState.adSettings === WorkState.Loading) {
      return (
        <div className={shared.spinnerWrapper}>
          <Spinner isLoading={true} spinnerStyle={spinnerStyle} />
        </div>
      );
    } else {
      return (
        <>
          <SkeletonTheme color="#202020" highlightColor="#444">
            {!isRegistered && (
              <>
                <p className={shared.subtext}>
                  In order to get paid out for ad revenue earned through Adbox
                  monetization you will need to create a Hyperwallet account.
                  Simply fill out form below and we will take care of the rest!
                </p>
                <AccountStatus />
                <UserPayPortalForm
                  isRegistered={isRegistered}
                  userInfo={userInfo}
                  setShowForm={setShowForm}
                  showForm
                />
              </>
            )}
            {isRegistered && !showForm && (
              <>
                <AccountStatus />
                <CurrentInfoWidget />
                {/* <button
                className={styles.updateBtn}
                onClick={() => setShowForm(!showForm)}
              >
                Change
              </button> */}
                {!(localStorage.getItem("closedSetupWidget") === "true") && (
                  <SetupWidget />
                )}
                {!(localStorage.getItem("closedSupportWidget") === "true") && (
                  <SupportWidget />
                )}
              </>
            )}

            {isRegistered && showForm && (
              <>
                <AccountStatus />
                <UserPayPortalForm
                  isRegistered={isRegistered}
                  userInfo={userInfo}
                  setShowForm={setShowForm}
                  showForm
                />
              </>
            )}
          </SkeletonTheme>
        </>
      );
    }
  };

  return (
    <div className={shared.subHeader}>
      <h2>Settings</h2>
      {errorMessage ? (
        <div className={shared.errorContainer}>
          <p>{errorMessage}</p>{" "}
        </div>
      ) : (
        renderUI()
      )}
    </div>
  );
}
